import { createAction, createReducer } from '@reduxjs/toolkit';
import { get } from 'lodash-es';
import commonBaseService from '../../../services/commonBaseService';

const MODULE_PREFIX = '[ STOCKS PORTFOLIO ]';

const Actions = {
	fetchHoldings: `${MODULE_PREFIX} fetchHoldings`,
	setHoldings: `${MODULE_PREFIX} setHoldings`,
	setIsOverAllPnl: `${MODULE_PREFIX} setIsOverAllPnl`,
	setSelectedTab: `${MODULE_PREFIX} setSelectedTab`,
	fetchPositions: `${MODULE_PREFIX} fetchPositions`,
	setPositions: `${MODULE_PREFIX} setPositions`,
	fetchInvestmentHistory: `${MODULE_PREFIX} fetchInvestmentHistory`,
	setInvestmentHistory: `${MODULE_PREFIX} setInvestmentHistory`,
	setSelectedHoldingIsin: `${MODULE_PREFIX} setSelectedHoldinIsin`,
	setPositionsExcludedItems: `${MODULE_PREFIX} setPositionsExcludedItems`,
	setSelectedPositionId: `${MODULE_PREFIX} setSelectedPositionId`,
	fetchRealisedPnl: `${MODULE_PREFIX} fetchRealisedPnl`,
	setRealisedPnl: `${MODULE_PREFIX} setRealisedPnl`,
	setSelectedSquareoffIds: `${MODULE_PREFIX} setSelectedSquareoffIds`,
	addSquareoffId: `${MODULE_PREFIX} addSquareoffId`,
	removeSquareoffId: `${MODULE_PREFIX} removeSquareoffId`,
	clearAllSelectedSquareoffIds: `${MODULE_PREFIX} clearAllSelectedSquareoffIds`,
	squareoffSelectedPostions: `${MODULE_PREFIX} squareoffSelectedPositions`,
	fetchHoldingsV1: `${MODULE_PREFIX} fetchHoldingsV1`,
	setHoldingsV1: `${MODULE_PREFIX} setHoldingsV1`,

	// EDIS
	fetchEdisSummary: `${MODULE_PREFIX} fetchEdisSummary`,
	setEdisSummary: `${MODULE_PREFIX} setEdisSummary`,
	fetchEdisUrl: `${MODULE_PREFIX} fetchEdisUrl`,
	fetchDpDetails: `${MODULE_PREFIX} fetchdpDetails`,
	generateNewTpin: `${MODULE_PREFIX} generateNewTpin`,
	setIsConvertPositionInProgress: `${MODULE_PREFIX} setIsConvertPositionInProgress`,
	convertPosition: `${MODULE_PREFIX} convertPosition`,
};

// Actions
export const fetchHoldings = createAction(Actions.fetchHoldings);
export const setHoldings = createAction(Actions.setHoldings);
export const setIsOverAllPnl = createAction(Actions.setIsOverAllPnl);
export const setSelectedTab = createAction(Actions.setSelectedTab);
export const fetchPositions = createAction(Actions.fetchPositions);
export const setPositions = createAction(Actions.setPositions);
export const fetchInvestmentHistory = createAction(Actions.fetchInvestmentHistory);
export const setInvestmentHistory = createAction(Actions.setInvestmentHistory);
export const setSelectedHoldingIsin = createAction(Actions.setSelectedHoldingIsin);
export const setPositionsExcludedItems = createAction(Actions.setPositionsExcludedItems);
export const setSelectedPositionId = createAction(Actions.setSelectedPositionId);
export const fetchRealisedPnl = createAction(Actions.fetchRealisedPnl);
export const setRealisedPnl = createAction(Actions.setRealisedPnl);
export const setSelectedSquareoffIds = createAction(Actions.setSelectedSquareoffIds);
export const addSquareoffId = createAction(Actions.addSquareoffId);
export const removeSquareoffId = createAction(Actions.removeSquareoffId);
export const clearAllSelectedSquareoffIds = createAction(Actions.clearAllSelectedSquareoffIds);
export const squareoffSelectedPositions = createAction(Actions.squareoffSelectedPostions);
export const fetchHoldingsV1 = createAction(Actions.fetchHoldingsV1);
export const setHoldingsV1 = createAction(Actions.setHoldingsV1);
export const setIsConvertPositionInProgress = createAction(Actions.setIsConvertPositionInProgress);
export const convertPosition = createAction(Actions.convertPosition);

// EDIS actions
export const fetchEdisSummary = createAction(Actions.fetchEdisSummary);
export const setEdisSummary = createAction(Actions.setEdisSummary);
export const fetchEdisUrl = createAction(Actions.fetchEdisUrl);
export const fetchDpDetails = createAction(Actions.fetchDpDetails);
export const generateNewTpin = createAction(Actions.generateNewTpin);

// Selectors
export const getHoldingsData = (state) => {
	return get(state, 'stocksPortfolio.holdings', {});
};

export const getHoldingsExcludedFromSearch = (state) => {
	return get(state, 'stocksPortfolio.holdingsExcludedFromSearch', new Set());
};

export const getPositionsExcludedFromSearch = (state) => {
	return get(state, 'stocksPortfolio.positionsExcludedFromSearch', new Set());
};

export const getIsOverAllPnl = (state) => {
	return get(state, 'stocksPortfolio.isOverAllPnl', false);
};

export const getPfSelectedTab = (state) => {
	return get(state, 'stocksPortfolio.selectedTab', 'Holdings');
};

export const getPositions = (state) => {
	return get(state, 'stocksPortfolio.positions', {});
};

export const getInvestmentHistoryData = (state) => get(state, 'stocksPortfolio.investmentHistory', []);
export const getSelectedHoldingIsin = (state) => {
	return state.stocksPortfolio.selectedHoldingIsin;
};

export const getSelectedHoldingsDetails = (state) => {
	const holdingsListData = get(state, 'stocksPortfolio.holdings.data.holdings', {});
	const selectedHoldingIsin = get(state, 'stocksPortfolio.selectedHoldingIsin', '');
	const selectedHoldingDetails = get(holdingsListData, `${selectedHoldingIsin}`, {});
	return selectedHoldingDetails;
};

export const getSelectedPositionId = (state) => {
	return get(state, 'stocksPortfolio.selectedPositionId', '');
};

export const getSelectedPositionDetails = (state) => {
	const selectedPositionId = getSelectedPositionId(state);
	const positionListData = getPositions(state);
	if (
		!commonBaseService.isEmptyOrUndefined(selectedPositionId) &&
		!commonBaseService.isEmptyOrUndefined(positionListData)
	) {
		const selectedPositionDetails = get(positionListData, `data.positions.${selectedPositionId}`, {});
		return selectedPositionDetails;
	}
	return {};
};

export const getRealisedPnlCash = (state) => get(state, 'stocksPortfolio.realisedPnl.cash', {});
export const getRealisedPnlFno = (state) => get(state, 'stocksPortfolio.realisedPnl.fno', {});
export const getRealisedPnlTransactions = (state, flow) => {
	return get(state, `stocksPortfolio.realisedPnl.${flow}.data.transactions`, []);
};

export const getSelectedSquareoffIds = (state) => get(state, 'stocksPortfolio.squareOffIds', []);

export const getEdisSummaryData = (state) => get(state, 'stocksPortfolio.edisSummary', []);

export const getHoldingV1Data = (state) => get(state, 'stocksPortfolio.holdingsV1Data', []);
export const getIsConvertPositionInProgress = (state) => get(state, 'stocksPortfolio.isConvertPositionInProgress', false);

// initial state
const INITIAL_STATE = {
	holdings: {
		isLoading: true,
		isRefreshing: false,
		isFetchFailed: false,
		errorMessage: '',
		data: {},
	},
	positions: {
		isLoading: true,
		isRefreshing: false,
		isFetchFailed: false,
		errorMessage: '',
		data: {},
	},
	investmentHistory: {
		isLoading: true,
		isFetchFailed: false,
		errorMessage: '',
		data: [],
	},
	realisedPnl: {
		cash: {
			isLoading: false,
			isError: false,
			data: {},
			message: '',
		},
		fno: {
			isLoading: false,
			isError: false,
			data: {},
			message: '',
		},
	},
	selectedTab: 'Holdings',
	holdingsExcludedFromSearch: [],
	positionsExcludedFromSearch: [],
	holdingsExcludedItems: [],
	positionsExcludedItems: [],
	selectedPositionId: '',
	isOverAllPnl: true,
	selectedHoldingIsin: '',
	squareOffIds: [],
	edisSummary: [],
	holdingsV1Data: [],
	isConvertPositionInProgress: false,
};

// reducer
export default createReducer(INITIAL_STATE, (builder) => {
	builder.addCase(setHoldings, (state, { payload }) => {
		state.holdings = { ...state.holdings, ...payload };
	});
	builder.addCase(setIsOverAllPnl, (state, { payload }) => {
		state.isOverAllPnl = payload;
	});
	builder.addCase(setSelectedTab, (state, { payload }) => {
		state.selectedTab = payload;
	});
	builder.addCase(setPositions, (state, { payload }) => {
		state.positions = { ...state.positions, ...payload };
	});
	builder.addCase(setInvestmentHistory, (state, { payload }) => {
		state.investmentHistory = { ...state.investmentHistory, ...payload };
	});
	builder.addCase(setSelectedHoldingIsin, (state, { payload }) => {
		state.selectedHoldingIsin = payload;
	});
	builder.addCase(setSelectedPositionId, (state, { payload }) => {
		state.selectedPositionId = payload;
	});
	builder.addCase(setRealisedPnl, (state, action) => {
		const { payload = {}, type } = action.payload;
		const prevRealisedValue = get(state, `realisedPnl.${type}`, {});
		state.realisedPnl[type] = { ...prevRealisedValue, ...payload };
	});
	builder.addCase(setSelectedSquareoffIds, (state, { payload }) => {
		state.squareOffIds = payload;
	});
	builder.addCase(addSquareoffId, (state, { payload }) => {
		const positionId = payload;
		if (!state.squareOffIds.includes(positionId)) {
			state.squareOffIds = [...state.squareOffIds, positionId];
		}
	});
	builder.addCase(removeSquareoffId, (state, { payload }) => {
		const positionId = payload;
		if (state.squareOffIds.includes(positionId)) {
			state.squareOffIds = state.squareOffIds.filter((item) => item !== positionId);
		}
	});
	builder.addCase(clearAllSelectedSquareoffIds, (state, { payload }) => {
		state.squareOffIds = [];
	});
	builder.addCase(setEdisSummary, (state, action) => {
		state.edisSummary = action.payload;
	});
	builder.addCase(setHoldingsV1, (state, action) => {
		state.holdingsV1Data = action.payload;
	});
	builder.addCase(setIsConvertPositionInProgress, (state, action) => {
		state.isConvertPositionInProgress = action.payload;
	});
});
